// The Packing Slip Preview backend does not aggregate Home Delivery orders
// the way slip generation does (arabesque PR #4982), so the preview would
// show one slip per customer order. This helper approximates the generated
// slips on the frontend: it merges Home Delivery preview slips per Cold
// Chain wholesale location, sums the quantities by SKU, and splits them
// into tote-sized one-SKU slips using the same jar-size data the summary
// uses. The final slips are still generated by the backend at allocation
// time — this is a preview approximation only.

export const HOME_DELIVERY_ORDER_TYPE = 'HOME_DELIVERY';

export function aggregateColdChainPreviewSlips({
  previewSlips,
  ordersById,
  toteSizes,
  items,
}) {
  const regularSlips = [];
  const groups = new Map();

  (previewSlips || []).forEach((slip) => {
    const order = ordersById?.[slip.orderId];
    const isColdChainHomeDelivery =
      order?.orderType === HOME_DELIVERY_ORDER_TYPE &&
      order.wholesaleLocationId;
    if (!isColdChainHomeDelivery) {
      regularSlips.push(slip);
      return;
    }

    let group = groups.get(order.wholesaleLocationId);
    if (!group) {
      group = { skuTotals: new Map(), template: slip };
      groups.set(order.wholesaleLocationId, group);
    }
    (slip.data || []).forEach(({ itemName, value, productionDate }) => {
      const current = group.skuTotals.get(itemName) || {
        value: 0,
        productionDate,
      };
      current.value += value;
      group.skuTotals.set(itemName, current);
    });
  });

  const aggregatedSlips = [];
  groups.forEach((group, wholesaleLocationId) => {
    const totes = [];
    group.skuTotals.forEach(({ value, productionDate }, itemName) => {
      const packagingType = items?.find(
        (item) => item.id === itemName,
      )?.packagingType;
      const maxPerTote = toteSizes?.[packagingType];
      if (!(maxPerTote >= 1)) {
        // unknown tote size — keep the quantity whole rather than guessing
        totes.push({ itemName, value, productionDate });
        return;
      }
      let remaining = value;
      while (remaining > 0) {
        totes.push({
          itemName,
          value: Math.min(remaining, maxPerTote),
          productionDate,
        });
        remaining -= maxPerTote;
      }
    });

    totes.forEach((tote, index) => {
      aggregatedSlips.push({
        ...group.template,
        location: `${wholesaleLocationId}&COLD_CHAIN_PREVIEW`,
        addressForDelivery: wholesaleLocationId,
        driver: 'Route data unavailable',
        stop: '0',
        clientCode: 'Delivery',
        // per-customer fields must not appear on the Cold Chain handoff slips
        deliverySpecialNotes: null,
        poNumber: null,
        palletUniqueId: null,
        uniqueId: `cold-chain-preview#${wholesaleLocationId}#${index + 1}`,
        qrCode: null,
        data: [tote],
        subPackingSlipIndex: index + 1,
        totalSubPackingSlips: totes.length,
      });
    });
  });

  return [...regularSlips, ...aggregatedSlips];
}
